import type { Extensions } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Highlight from '@tiptap/extension-highlight';
import { Markdown } from '@tiptap/markdown';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import Image from '@tiptap/extension-image';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import CharacterCount from '@tiptap/extension-character-count';
import Youtube from '@tiptap/extension-youtube';
import Color from '@tiptap/extension-color';
// @tiptap/extension-text-style has no default export in v3 — TextStyle is a
// named export (importing it as default throws at module load → white page).
import { TextStyle } from '@tiptap/extension-text-style';
import { SlashSuggestionExtension } from './slash-menu/slash-suggestion';
import { MarkdownPaste } from './markdown-paste';
import { MarkdownCopy } from './markdown-copy';
import { YoutubePaste } from './youtube-paste';
import { EditorShortcuts } from './editor-shortcuts';
import { NulaLinkExtension } from './nula-link-extension';
import { LinkClickExtension } from './link-click-extension';
import { DocumentLink } from './document-link-extension';
import { CalloutExtension } from './callout-extension';
import { CollapsibleExtensions } from './collapsible-extension';
import { CommentMark } from './comment-extension';
import { EmojiExtension } from './emoji-extension';
import { MarkdownCodeBlock, markdownTableKit } from './markdown-blocks';
import { lowlight } from './lowlight';
import { CodeBlockToolbarExtension } from './code-block-toolbar-extension';
import { MermaidExtension } from './mermaid-extension';
import { AutoJoinerExtension } from './auto-joiner-extension';
import { TrailingParagraph } from './trailing-paragraph-extension';
import { ReadOnlyGuard } from './read-only-guard-extension';
import { LinkUnfurlExtension } from './link-unfurl-extension';
import { CanvasExtension } from './canvas-extension';
import { LinkUnfurlPaste } from './link-unfurl-paste';
import { LinkUnfurlInput } from './link-unfurl-input';
import { AiEditing } from '../extensions/ai-editing/ai-editing';

export interface EditorContentExtensionsOptions {
  onNavigate: (id: string) => void;
  onAnchor: (id: string) => void;
}

/**
 * The RichTextEditor's content extensions for a collaborative doc, without
 * Collaboration and CollaborationCaret (the caller binds those to its Y.Doc).
 */
export function editorContentExtensions({ onNavigate, onAnchor }: EditorContentExtensionsOptions): Extensions {
  return [
    // StarterKit v3 already bundles Link, Underline, Bold, Italic,
    // headings, lists, blockquote, code block, HR, etc. Do NOT
    // register Link/Underline separately — that creates duplicate
    // extensions (a real bug that breaks paste/input handling).
    // codeBlock: false — replaced by CodeBlockLowlight (highlight.js
    // syntax highlighting via the shared `lowlight` instance). We do
    // NOT use tiptap-extension-code-block-shiki: it re-tokenized the
    // whole doc and dispatched a force-decoration transaction on every
    // keystroke, which y-prosemirror Collaboration amplified to ~10
    // transactions/key — freezing the editor. The per-block language
    // picker / copy / delete toolbar is a ProseMirror widget
    // decoration (CodeBlockToolbarExtension), NOT a React node view.
    // link: false — replaced by DocumentLink (Link.extend) which tags
    // each <a> with data-link-type for the link-kind icon and allows
    // the nula:// scheme so document links (nula://document/<id>,
    // emitted by the agent + the doc picker) survive sanitization.
    // LinkClickExtension routes their clicks to in-app navigation.
    // trailingNode: false — replaced by TrailingParagraph, which acts only
    // on the user's own edits, never on content received from Yjs.
    StarterKit.configure({
      undoRedo: false,
      codeBlock: false,
      link: false,
      trailingNode: false,
    }),
    TrailingParagraph,
    DocumentLink.configure({ protocols: ['nula'] }),
    MarkdownCodeBlock.configure({ lowlight }),
    CodeBlockToolbarExtension,
    MermaidExtension,
    LinkClickExtension.configure({ onNavigate, onAnchor }),
    TaskList,
    TaskItem.configure({ nested: true }),
    Highlight.configure({ multicolor: true }),
    ...markdownTableKit({ resizable: true }),
    TextStyle,
    Color,
    TextAlign.configure({ types: ['heading', 'paragraph'] }),
    Image,
    Subscript,
    Superscript,
    CharacterCount,
    Youtube,
    Placeholder.configure({ placeholder: "Type '/' for commands" }),
    Markdown,
    MarkdownPaste,
    MarkdownCopy,
    YoutubePaste,
    NulaLinkExtension,
    EmojiExtension,
    CalloutExtension,
    CommentMark,
    ...CollapsibleExtensions,
    EditorShortcuts,
    AutoJoinerExtension.configure({ nodeTypes: ['bulletList', 'orderedList'] }),
    LinkUnfurlExtension,
    LinkUnfurlPaste,
    LinkUnfurlInput,
    CanvasExtension,
    SlashSuggestionExtension,
    AiEditing,
    ReadOnlyGuard,
  ];
}
